import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { Diamond } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email is required');
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }

    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#B8860B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sign In — Our Day</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Lato:wght@300;400;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-[#F5F2EA]" style={{ fontFamily: "'Lato', sans-serif" }}>
        {/* Glass Nav */}
        <nav className="fixed top-0 left-0 w-full z-50 border-b border-[#E8DCCA]/50" style={{ background: 'rgba(253,252,248,0.9)', backdropFilter: 'blur(12px)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <Link to="/" className="flex items-center gap-3">
                <Diamond className="w-7 h-7 text-[#B8860B]" />
                <span className="text-2xl font-bold tracking-wide text-[#2C2C2C]" style={{ fontFamily: "'Playfair Display', serif" }}>Our Day</span>
              </Link>
              <div className="flex items-center space-x-6">
                <span className="text-sm text-[#555] hidden sm:inline">Don't have an account?</span>
                <Link to="/register" className="text-[#B8860B] hover:text-[#D4AF37] font-medium text-sm uppercase tracking-widest transition-colors">
                  Sign Up
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <div className="flex-grow flex items-center justify-center relative w-full pt-20 pb-12">
          {/* Blurred gradient orbs */}
          <div className="absolute top-[-10%] left-[-5%] w-[600px] h-[600px] bg-[#E8DCCA]/30 rounded-full blur-[100px] opacity-60 pointer-events-none" />
          <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#B8860B]/10 rounded-full blur-[80px] opacity-40 pointer-events-none" />

          <div className="w-full max-w-md mx-4 relative z-10">
            <div className="bg-[#FDFCF8] p-8 sm:p-12 rounded-sm shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] border border-[#E8DCCA]/60 flex flex-col items-center">
              {/* Diamond Icon */}
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F5F2EA] border border-[#B8860B]/20 mb-6 text-[#B8860B] shadow-sm">
                  <Diamond className="w-8 h-8" />
                </div>
                <h1 className="text-3xl text-[#2C2C2C] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Welcome Back</h1>
                <p className="text-[#555] font-light text-sm">Please enter your details to access your account.</p>
              </div>

              <form onSubmit={handleSubmit} className="w-full space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm text-sm">
                    {error}
                  </div>
                )}

                {/* Email */}
                <div className="space-y-1">
                  <label htmlFor="email" className="block text-sm font-medium text-[#2C2C2C]/80 tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="block w-full px-4 py-3 bg-white border border-[#D1C0A8]/50 text-[#2C2C2C] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#B8860B] focus:border-[#B8860B] sm:text-sm rounded-sm transition-all"
                  />
                </div>

                {/* Password */}
                <div className="space-y-1">
                  <label htmlFor="password" className="block text-sm font-medium text-[#2C2C2C]/80 tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="block w-full px-4 py-3 bg-white border border-[#D1C0A8]/50 text-[#2C2C2C] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#B8860B] focus:border-[#B8860B] sm:text-sm rounded-sm transition-all"
                  />
                </div>

                {/* Remember me + Forgot password */}
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      type="checkbox"
                      className="h-4 w-4 text-[#B8860B] focus:ring-[#D4AF37]/50 border-gray-300 rounded cursor-pointer"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-[#555] cursor-pointer select-none">Remember me</label>
                  </div>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#B8860B] hover:text-[#D4AF37] transition-colors"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-sm shadow-md text-sm font-bold text-[#2C2C2C] bg-[#E8DCCA] hover:bg-[#D1C0A8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B8860B] uppercase tracking-widest transition-all hover:shadow-lg active:scale-[0.99] disabled:opacity-50"
                    style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '0.05em' }}
                  >
                    {submitting ? (
                      <div className="w-5 h-5 border-2 border-[#2C2C2C] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </div>
              </form>

              {/* Social divider */}
              <div className="mt-8 w-full">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#D1C0A8]/30" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[#FDFCF8] text-slate-400 font-light">Or continue with</span>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <button type="button" className="w-full inline-flex justify-center py-2.5 px-4 border border-[#D1C0A8]/40 rounded-sm shadow-sm bg-white text-sm font-medium text-[#2C2C2C] hover:bg-gray-50 transition-colors">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
                    </svg>
                  </button>
                  <button type="button" className="w-full inline-flex justify-center py-2.5 px-4 border border-[#D1C0A8]/40 rounded-sm shadow-sm bg-white text-sm font-medium text-[#2C2C2C] hover:bg-gray-50 transition-colors">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.25.183c.641-.89 2.47-2.009 3.79-1.309.894.475 1.517 1.458 1.258 2.65-.544 2.503-3.235 2.525-4.14 1.503-.64-.722-.505-1.956-.908-2.844zm-7.98 1.41c1.554.025 2.678.835 3.393.835.715 0 2.217-.98 4.028-.813 1.096.1 3.253.513 4.675 2.768-.12.075-2.793 1.638-2.768 4.793.025 3.528 3.193 4.728 3.303 4.768-.025.075-.515 1.768-1.703 3.493-1.028 1.49-2.103 2.978-3.793 3.003-1.653.025-2.193-1.003-4.103-1.003-1.93 0-2.543 1.053-4.153.978-1.653-.075-2.928-1.688-3.993-3.213-2.178-3.138-3.833-8.878-1.603-12.728 1.078-1.878 3.018-3.053 5.118-3.078z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom links */}
            <div className="text-center mt-8">
              <span className="text-xs text-[#B8860B]/70 tracking-widest uppercase">Privacy Policy &bull; Terms of Service</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-[#E8DCCA] bg-[#FDFCF8] py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 opacity-60">
              <Diamond className="w-4 h-4 text-[#B8860B]" />
              <span className="text-xs italic text-[#2C2C2C] tracking-wider" style={{ fontFamily: "'Playfair Display', serif" }}>The Royal Standard in Invitations</span>
            </div>
            <div className="text-center text-xs text-slate-400 font-light tracking-wide">
              &copy; 2026 Our Day Inc. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LoginPage;
